<script setup>
  import {computed, nextTick, onBeforeMount, ref, watch} from "vue";

  import ComponentDosageInput from "@components/Inputs/DosageInput.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentSwitch from "@components/Switch.vue";
  import ComponentTimeSelect from "@components/TimeSelect.vue";
  import {daysNumber, monthNumber} from "@enums/medicationEnums.js";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "MedicationComponentDetailDialogMultipleDosageInput",
  });

  const props = defineProps({
    dataHashId: {
      type: String,
      required: true,
    },
    dosageForm: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    intervalValue: {
      type: [String, Number],
      required: true,
    },
  });

  const emit = defineEmits(["isInvalid", "change", "isValid"]);

  const id = ref(null);
  const dataList = ref([]);
  const valueForAllTimes = ref(false);

  // computed instead of a value copied in onBeforeMount, so changing the
  // medication's unit above updates the unit shown next to the dosage inputs
  const unit = computed(() => props.dosageForm.selected_unit ?? "");

  const lastEnteredValue = ref({
    dosageValue: 0,
    dosageValueAsFraction: false,
  });

  watch(
    () => props.intervalValue,
    () => {
      dataList.value.splice(0);
      valueForAllTimes.value = false;
      updateDataList();
      // immediately emit change event (the null values, which are
      // represented with "0", will always be stored)
      nextTick(() => {
        emit("change", {newData: dataList.value});
      });
    },
  );

  onBeforeMount(() => {
    id.value = `special-dosage -${Math.floor(Math.random() * 1_000_000)}`.replaceAll(" ", "");

    updateDataList();
    // immediately emit change event (the null values, which are
    // represented with "1", will always be stored)
    nextTick(() => {
      emit("change", {newData: dataList.value});
    });
  });

  const updateDataList = () => {
    for (let i = 0; i <= props.intervalValue - 1; i++) {
      let dataListObject = cloneDeep(props.dosageForm?.dosagealternative?.dosagealternativedatas?.[i]) ?? {
        dayofmonth: null,
        month: null,
        time: null,
        value: 1,
        value_as_fraction: false,
        weekday: null,
      };

      dataList.value.push(dataListObject);
    }
  };

  const handleInput = ({as_fraction, id, value}, index) => {
    dataList.value[index].value = value;
    dataList.value[index].value_as_fraction = as_fraction;

    lastEnteredValue.value.dosageValue = value;
    lastEnteredValue.value.dosageValueAsFraction = as_fraction;

    valueForAllTimes.value =
      new Set(
        dataList.value.map((n) => {
          return n.value.toString() + n.value_as_fraction;
        }),
      ).size === 1;

    emit("isValid", id);
    emit("change", {newData: dataList.value});
  };

  const handleWeekDay = (newWeekDay, index) => {
    dataList.value[index].weekday = newWeekDay;

    emit("change", {newData: dataList.value});
  };

  const handleMonth = (newMonth, index) => {
    dataList.value[index].month = newMonth;

    emit("change", {newData: dataList.value});
  };

  const handleTimeInput = ({time}, index) => {
    dataList.value[index].time = time;

    emit("change", {newData: dataList.value});
  };

  const handleDayOfMonth = (dayOfMonth, index) => {
    dataList.value[index].dayofmonth = dayOfMonth;

    emit("change", {newData: dataList.value});
  };

  const setValueForAllTimes = (newValue) => {
    valueForAllTimes.value = newValue;

    if (valueForAllTimes.value) {
      dataList.value.map((item) => {
        item.value = lastEnteredValue.value.dosageValue;
        item.value_as_fraction = lastEnteredValue.value.dosageValueAsFraction;
      });
    }
  };
</script>

<template>
  <ul>
    <li v-for="(item, index) in dataList" :key="index" class="flex items-baseline space-x-4">
      <ComponentDosageInput
        :id="'specialDosage' + dataHashId + index"
        :name="'specialDosage' + dataHashId + index"
        :value="item.value"
        :as-fraction="item.value_as_fraction"
        :test-id="getTestId(`multiple-${index}`)"
        @input="handleInput($event, index)"
        @is-invalid="$emit('isInvalid', $event)"
      />

      <div class="truncate pt-2 text-sm">
        {{ unit }}
      </div>

      <template v-if="type === 'specialMonth'">
        <ComponentSelect
          v-model="item.month"
          class="w-28!"
          select-box-width="w-28"
          :options="monthNumber"
          :test-id="getTestId(`multiple-${index}-month`)"
          @change="handleMonth($event, index)"
        />
      </template>

      <template v-if="type === 'multipleWeek' || type === 'specialMonth'">
        <ComponentSelect
          v-model="item.weekday"
          class="w-28!"
          :options="daysNumber"
          :test-id="getTestId(`multiple-${index}-days`)"
          @change="handleWeekDay($event, index)"
        />
      </template>

      <template v-if="!['multipleHours', 'specialWeeks', 'specialMonth', 'multipleMonth'].includes(type)">
        <ComponentTimeSelect
          :value="item.time"
          :test-id="getTestId(`multiple-${index}-time`)"
          @selected-time="handleTimeInput($event, index)"
        />
      </template>

      <template v-if="type === 'multipleMonth'">
        <div class="w-16">
          <ComponentInput
            v-model="item.dayofmonth"
            input-alignment="text-center"
            @input="handleDayOfMonth($event, index)"
          />
        </div>

        <div class="text-sm">. Tag des Monats</div>
      </template>
    </li>

    <ComponentSwitch
      v-if="dataList.length > 1"
      v-model="valueForAllTimes"
      class="mt-6"
      color="accent"
      label="Für alle Zeitpunkte übernehmen"
      :test-id="getTestId('multiple-switch')"
      @input="setValueForAllTimes"
    />
  </ul>
</template>
